/**
 * Agente CEO — acceso a datos del módulo (F1).
 *
 * Implementa la sección 3 de `docs/spec-agente-ceo-portal-alta-direccion.md`:
 * todo lo que el front lee sale de tablas `ia_` con RLS, y **nada se escribe por
 * la tabla**: cada escritura pasa por una RPC `SECURITY DEFINER` que valida el
 * permiso adentro. Si el front intentara un `insert` directo, Postgres lo
 * rechazaría — es a propósito.
 *
 * Mientras no se ejecute `Ejecuciones_manuales/20260925_ia_agente_ceo_f1.md`
 * las tablas no existen; `useAgenteCeoDisponible()` lo detecta y la UI muestra
 * el aviso en vez de tronar (patrón de DDL probe del CLAUDE.md).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const TABLA_INEXISTENTE = "PGRST205";
/** PostgREST responde PGRST202 cuando no encuentra la RPC; 42883 es el código de Postgres. */
const FUNCION_INEXISTENTE = new Set(["PGRST202", "42883"]);

export const CODIGO_AGENTE = "bot-agente-alta-direccion";

/**
 * Id del agente CEO en `ia_agentes`. `ia_conversaciones` la comparten todos los agentes (la
 * guía de los portales escribe ahí también), así que cada lectura de este módulo filtra por él;
 * sin el filtro, las conversaciones de la guía aparecían en la lista y en el uso del CEO.
 */
async function idAgenteCeo(): Promise<number | null> {
  const { data, error } = await (supabase as any)
    .from("ia_agentes").select("id").eq("codigo", CODIGO_AGENTE).maybeSingle();
  if (error || data?.id == null) return null;
  return Number(data.id);
}

export type EtapaDisponibilidad = "listo" | "sin_migracion" | "sin_permiso";

export interface AgenteConfig {
  id: number;
  codigo: string;
  nombre: string;
  modelo: string;
  modeloProfundo: string;
  maxTokensRespuesta: number;
  maxIteraciones: number;
  presupuestoTokensMemoria: number;
  limiteMensajesHora: number;
  herramientasHabilitadas: string[];
}

export interface ContextoVersion {
  id: number;
  version: number | null;
  contenido: string;
  notaCambio: string | null;
  estado: "borrador" | "publicada";
  fechaCreacion: string;
  fechaPublicacion: string | null;
}

export interface Memoria {
  id: number;
  tipo: "regla" | "definicion" | "preferencia" | "hecho" | "foco";
  contenido: string;
  alcance: "global" | "usuario";
  estado: "propuesta" | "activa" | "rechazada" | "archivada";
  origen: string;
  prioridad: number;
  justificacion: string | null;
  vigenteHasta: string | null;
  vecesCitada: number;
  idConversacion: string | null;
  propuestoPor: string;
  fechaCreacion: string;
}

export interface Conversacion {
  id: string;
  titulo: string | null;
  fijada: boolean;
  visibilidad: "privada" | "compartida";
  esPrueba: boolean;
  idUsuario: string;
  /** Nombre de quien la inició. Una conversación tiene un solo dueño. */
  duenoNombre: string | null;
  esMia: boolean;
  estado: "activa" | "archivada";
  fechaUltimoMensaje: string | null;
  fechaCreacion: string;
}

export interface MensajeGuardado {
  id: string;
  orden: number;
  rol: "usuario" | "asistente";
  contenido: string;
  modelo: string | null;
  herramientas: Array<{ nombre: string; argumentos?: unknown; ms?: number; error?: string | null }>;
  memoriasCitadas: number[];
  latenciaMs: number | null;
  fechaCreacion: string;
}

/** Distingue "falta la migración" de "no tienes permiso": la UI dice cosas distintas. */
export function useAgenteCeoDisponible() {
  return useQuery<EtapaDisponibilidad>({
    queryKey: ["ia-agente-disponible"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { error } = await (supabase as any).from("ia_agentes").select("id").limit(1);
      if (error) {
        if (error.code === TABLA_INEXISTENTE) return "sin_migracion";
        throw new Error(error.message);
      }
      const { data, error: errorRpc } = await (supabase as any).rpc("ia_direccion_puede", { p_nivel: "usar" });
      if (errorRpc) {
        if (FUNCION_INEXISTENTE.has(errorRpc.code)) return "sin_migracion";
        throw new Error(errorRpc.message);
      }
      return data === true ? "listo" : "sin_permiso";
    },
  });
}

/** `administrar` = pestaña de Configuración. Se consulta aparte porque no todos la tienen. */
export function usePuedeAdministrarAgente() {
  return useQuery<boolean>({
    queryKey: ["ia-agente-administrar"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("ia_direccion_puede", { p_nivel: "administrar" });
      if (error) return false;
      return data === true;
    },
  });
}

export function useAgenteConfig() {
  return useQuery<AgenteConfig | null>({
    queryKey: ["ia-agente-config"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ia_agentes").select("*").eq("codigo", CODIGO_AGENTE).maybeSingle();
      if (error || !data) return null;
      return {
        id: Number(data.id),
        codigo: data.codigo,
        nombre: data.nombre,
        modelo: data.modelo,
        modeloProfundo: data.modelo_profundo,
        maxTokensRespuesta: data.max_tokens_respuesta,
        maxIteraciones: data.max_iteraciones,
        presupuestoTokensMemoria: data.presupuesto_tokens_memoria,
        limiteMensajesHora: data.limite_mensajes_hora,
        herramientasHabilitadas: data.herramientas_habilitadas ?? [],
      };
    },
  });
}

/* ─────────────────────────── Contexto base ─────────────────────────────── */

export function useContextoVersiones() {
  return useQuery<ContextoVersion[]>({
    queryKey: ["ia-contexto-versiones"],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ia_contexto_versiones")
        .select("id, version, contenido, nota_cambio, estado, fecha_creacion, fecha_publicacion")
        .order("estado", { ascending: true })
        .order("version", { ascending: false, nullsFirst: true });
      if (error) return [];
      return (data ?? []).map((v: any) => ({
        id: Number(v.id),
        version: v.version ?? null,
        contenido: v.contenido,
        notaCambio: v.nota_cambio ?? null,
        estado: v.estado,
        fechaCreacion: v.fecha_creacion,
        fechaPublicacion: v.fecha_publicacion ?? null,
      }));
    },
  });
}

export function useGuardarBorradorContexto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contenido: string) => {
      const { error } = await (supabase as any).rpc("ia_guardar_borrador_contexto", { p_contenido: contenido });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-contexto-versiones"] }),
  });
}

export function usePublicarContexto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notaCambio: string) => {
      const { data, error } = await (supabase as any).rpc("ia_publicar_contexto", { p_nota_cambio: notaCambio });
      if (error) throw new Error(traduce(error));
      return data as number;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-contexto-versiones"] }),
  });
}

export function useRestaurarContexto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (version: number) => {
      const { error } = await (supabase as any).rpc("ia_restaurar_contexto", { p_version: version });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-contexto-versiones"] }),
  });
}

/* ───────────────────────────── Memoria ─────────────────────────────────── */

export function useMemorias() {
  return useQuery<Memoria[]>({
    queryKey: ["ia-memorias"],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ia_memorias")
        .select("id, tipo, contenido, alcance, estado, origen, prioridad, justificacion, vigente_hasta, veces_citada, id_conversacion, propuesto_por, fecha_creacion")
        .order("estado", { ascending: true })
        .order("prioridad", { ascending: true })
        .order("fecha_creacion", { ascending: false });
      if (error) return [];
      return (data ?? []).map((m: any) => ({
        id: Number(m.id),
        tipo: m.tipo,
        contenido: m.contenido,
        alcance: m.alcance,
        estado: m.estado,
        origen: m.origen,
        prioridad: m.prioridad,
        justificacion: m.justificacion ?? null,
        vigenteHasta: m.vigente_hasta ?? null,
        vecesCitada: m.veces_citada ?? 0,
        idConversacion: m.id_conversacion ?? null,
        propuestoPor: m.propuesto_por,
        fechaCreacion: m.fecha_creacion,
      }));
    },
  });
}

export function useProponerMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      tipo: Memoria["tipo"]; contenido: string; alcance: Memoria["alcance"]; justificacion?: string | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("ia_proponer_memoria", {
        p_tipo: v.tipo,
        p_contenido: v.contenido,
        p_alcance: v.alcance,
        p_justificacion: v.justificacion ?? null,
        p_origen: "manual",
      });
      if (error) throw new Error(traduce(error));
      return data as { id: number; estado: string; duplicada: boolean };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-memorias"] }),
  });
}

export function useResolverMemoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: number; decision: "aprobar" | "rechazar" | "archivar";
      motivo?: string | null; contenido?: string | null; prioridad?: number | null; vigenteHasta?: string | null;
    }) => {
      const { error } = await (supabase as any).rpc("ia_resolver_memoria", {
        p_id: v.id,
        p_decision: v.decision,
        p_motivo: v.motivo ?? null,
        p_contenido: v.contenido ?? null,
        p_prioridad: v.prioridad ?? null,
        p_vigente_hasta: v.vigenteHasta ?? null,
      });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-memorias"] }),
  });
}

/* ──────────────────────── Conversaciones y mensajes ─────────────────────── */

export function useConversaciones() {
  return useQuery<Conversacion[]>({
    queryKey: ["ia-conversaciones"],
    staleTime: 15_000,
    queryFn: async () => {
      const { data: sesion } = await supabase.auth.getSession();
      const yo = sesion.session?.user?.id ?? null;
      const idAgente = await idAgenteCeo();
      if (idAgente === null) return [];

      const { data, error } = await (supabase as any)
        .from("ia_conversaciones")
        .select("id, titulo, fijada, visibilidad, es_prueba, estado, id_usuario, fecha_ultimo_mensaje, fecha_creacion")
        .eq("id_agente", idAgente)
        .neq("estado", "eliminada")
        .order("fijada", { ascending: false })
        .order("fecha_ultimo_mensaje", { ascending: false, nullsFirst: false })
        .limit(200);
      if (error) return [];

      // Quién inició cada una. Se resuelve en una sola consulta: en la lista se
      // ve "Compartida por Fulano", y sin el nombre eso no se puede decir.
      const ids = [...new Set((data ?? []).map((c: any) => c.id_usuario).filter(Boolean))];
      let nombrePorAuth = new Map<string, string>();
      if (ids.length) {
        const { data: usuarios } = await (supabase as any)
          .from("usuarios").select("auth_user_id, nombre").in("auth_user_id", ids);
        nombrePorAuth = new Map((usuarios ?? []).map((u: any) => [u.auth_user_id, u.nombre]));
      }

      return (data ?? []).map((c: any) => ({
        id: c.id,
        titulo: c.titulo ?? null,
        fijada: c.fijada,
        visibilidad: c.visibilidad,
        esPrueba: c.es_prueba,
        estado: c.estado,
        idUsuario: c.id_usuario,
        duenoNombre: nombrePorAuth.get(c.id_usuario) ?? null,
        esMia: !!yo && c.id_usuario === yo,
        fechaUltimoMensaje: c.fecha_ultimo_mensaje ?? null,
        fechaCreacion: c.fecha_creacion,
      }));
    },
  });
}

export function useMensajes(idConversacion: string | null) {
  return useQuery<MensajeGuardado[]>({
    queryKey: ["ia-mensajes", idConversacion],
    enabled: !!idConversacion,
    staleTime: 5_000,
    queryFn: async () => {
      try {
        return await leerMensajesCeo(idConversacion as string);
      } catch {
        return [];
      }
    },
  });
}

/** Mensajes guardados de una conversación (para pintarla o para repetir su pregunta). */
export async function leerMensajesCeo(idConversacion: string): Promise<MensajeGuardado[]> {
  const { data, error } = await (supabase as any)
    .from("ia_mensajes")
    .select("id, orden, rol, contenido, modelo, herramientas, memorias_citadas, latencia_ms, fecha_creacion")
    .eq("id_conversacion", idConversacion)
    .order("orden", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map((m: any) => ({
    id: m.id,
    orden: m.orden,
    rol: m.rol,
    contenido: m.contenido,
    modelo: m.modelo ?? null,
    herramientas: m.herramientas ?? [],
    memoriasCitadas: m.memorias_citadas ?? [],
    latenciaMs: m.latencia_ms ?? null,
    fechaCreacion: m.fecha_creacion,
  }));
}

/**
 * Qué mensajes están fuera del contexto. Se lee de la vista, no se recalcula en
 * el front: si el front y la Edge Function dedujeran el último evento por su
 * cuenta, tarde o temprano discreparían.
 */
export function useMensajesOcultos(idConversacion: string | null) {
  return useQuery<Set<string>>({
    queryKey: ["ia-mensajes-ocultos", idConversacion],
    enabled: !!idConversacion,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ia_mensajes_ocultos_vigentes")
        .select("id_mensaje");
      if (error) return new Set<string>(); // migración pendiente: nada oculto
      return new Set((data ?? []).map((m: any) => m.id_mensaje as string));
    },
  });
}

export function useOcultarMensaje() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { idMensaje: string; mostrar?: boolean; motivo?: string | null }) => {
      const { error } = v.mostrar
        ? await (supabase as any).rpc("ia_mostrar_mensaje", { p_id_mensaje: v.idMensaje })
        : await (supabase as any).rpc("ia_ocultar_mensaje", {
            p_id_mensaje: v.idMensaje, p_motivo: v.motivo ?? null,
          });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-mensajes-ocultos"] }),
  });
}

export function useActualizarConversacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string; titulo?: string | null; fijada?: boolean | null;
      visibilidad?: "privada" | "compartida" | null; estado?: "activa" | "archivada" | "eliminada" | null;
    }) => {
      const { error } = await (supabase as any).rpc("ia_actualizar_conversacion", {
        p_id: v.id,
        p_titulo: v.titulo ?? null,
        p_fijada: v.fijada ?? null,
        p_visibilidad: v.visibilidad ?? null,
        p_estado: v.estado ?? null,
      });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-conversaciones"] }),
  });
}

export function useCalificarMensaje() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { idMensaje: string; valor: 1 | -1; motivo?: string | null; comentario?: string | null }) => {
      const { error } = await (supabase as any).rpc("ia_calificar_mensaje", {
        p_id_mensaje: v.idMensaje,
        p_valor: v.valor,
        p_motivo: v.motivo ?? null,
        p_comentario: v.comentario ?? null,
      });
      if (error) throw new Error(traduce(error));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ia-uso"] }),
  });
}

/* ────────────────────────────── Uso ────────────────────────────────────── */

export interface UsoAgente {
  conversaciones: number;
  mensajes: number;
  pulgarArriba: number;
  pulgarAbajo: number;
  latenciaP50: number | null;
  brechas: Array<{ id: number; datoFaltante: string; pregunta: string; veces: number; estado: string }>;
}

export function useUsoAgente() {
  return useQuery<UsoAgente>({
    queryKey: ["ia-uso"],
    staleTime: 60_000,
    queryFn: async () => {
      const idAgente = await idAgenteCeo();
      if (idAgente === null) {
        return { conversaciones: 0, mensajes: 0, pulgarArriba: 0, pulgarAbajo: 0, latenciaP50: null, brechas: [] };
      }
      // `ia_mensajes` no tiene id_agente: se filtra por su conversación (un solo nivel de embed).
      // La retroalimentación no se filtra: solo el chat del CEO la captura (la guía no tiene pulgares).
      const [conv, msg, retro, brechas] = await Promise.all([
        (supabase as any).from("ia_conversaciones").select("id", { count: "exact", head: true })
          .eq("id_agente", idAgente).neq("estado", "eliminada"),
        (supabase as any).from("ia_mensajes").select("latencia_ms, ia_conversaciones!inner(id_agente)")
          .eq("ia_conversaciones.id_agente", idAgente).eq("rol", "asistente").limit(2000),
        (supabase as any).from("ia_retroalimentacion").select("valor"),
        (supabase as any).from("ia_brechas_datos").select("id, dato_faltante, pregunta, veces, estado")
          .eq("id_agente", idAgente).order("veces", { ascending: false }),
      ]);

      const latencias = ((msg.data ?? []) as any[])
        .map((m) => m.latencia_ms).filter((n: number | null): n is number => typeof n === "number")
        .sort((a, b) => a - b);

      const valores = (retro.data ?? []) as Array<{ valor: number }>;

      return {
        conversaciones: conv.count ?? 0,
        mensajes: (msg.data ?? []).length,
        pulgarArriba: valores.filter((v) => v.valor === 1).length,
        pulgarAbajo: valores.filter((v) => v.valor === -1).length,
        latenciaP50: latencias.length ? latencias[Math.floor(latencias.length / 2)] : null,
        brechas: ((brechas.data ?? []) as any[]).map((b) => ({
          id: Number(b.id),
          datoFaltante: b.dato_faltante,
          pregunta: b.pregunta,
          veces: b.veces,
          estado: b.estado,
        })),
      };
    },
  });
}

/** Traduce a algo que un director pueda leer; nunca se muestra un código crudo. */
function traduce(error: { code?: string; message?: string }): string {
  if (error.code === TABLA_INEXISTENTE || FUNCION_INEXISTENTE.has(error.code ?? "")) {
    return "Falta ejecutar la migración del Agente CEO en la base de datos.";
  }
  if (error.code === "42501" || /sin_permiso/.test(error.message ?? "")) {
    return "No tienes permiso para esta acción. Pídelo a un administrador.";
  }
  if (/limite_propuestas_diario/.test(error.message ?? "")) {
    return "Llegaste al límite de propuestas por día (20).";
  }
  return error.message ?? "No se pudo completar la acción.";
}
